import React, {
	createContext,
	Dispatch,
	SetStateAction,
	useContext,
	useEffect,
	useState,
} from 'react'

import { onAuthStateChanged } from 'firebase/auth'
import { useLocation, useNavigate } from 'react-router-dom'

import { auth } from 'config/firebase'
import { ROLE_LEVEL } from 'constants/roles'
import { useMaterialUIController } from 'context/materialUI'
import SplashScreen from 'layouts/splash'
import { ROUTE_PATHS } from 'routes'
import { handleClientSigning } from 'utils/signing'

import { AuthContextUser } from './types'

const AuthContext = createContext<{
	user: AuthContextUser
	setUser: Dispatch<SetStateAction<AuthContextUser>>
	loading: boolean
}>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
	const [controller, dispatch] = useMaterialUIController()
	const location = useLocation()
	const [user, setUser] = useState<AuthContextUser | null>()
	const [loading, setLoading] = useState(true)
	const navigate = useNavigate()

	useEffect(() => {
		const unsubscribe = onAuthStateChanged(auth, async firebaseUser => {
			if (firebaseUser) {
				const userData = await handleClientSigning({
					user: firebaseUser,
					dispatch,
					routes: controller.routes,
					setUser,
				})

				if (userData) {
					if (location.pathname === '/' || location.pathname === ROUTE_PATHS.AUTHENTICATION.SIGN_IN)
						navigate(
							ROLE_LEVEL[4].includes(userData.role)
								? ROUTE_PATHS.DASHBOARD.ANALYTICS
								: ROUTE_PATHS.FEEDBACK.NEW
						)
				}
				setLoading(false)
			} else {
				setLoading(true)
				setTimeout(() => {
					setUser(null)
					setLoading(false)
				}, 1000)
			}
		})

		return () => unsubscribe()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<AuthContext.Provider value={{ user, setUser, loading }}>
			{!loading ? children : <SplashScreen />}
		</AuthContext.Provider>
	)
}

export function useAuth() {
	return useContext(AuthContext)
}
